import { WorkflowStep } from '../types/workflow';

export interface AIProvider {
  id: string;
  name: string;
  description: string;
  icon: string;
  template: Omit<WorkflowStep, 'name'>;
}

export const AI_PROVIDERS: AIProvider[] = [
  {
    id: 'openai',
    name: 'OpenAI GPT',
    description: 'GPT-4, GPT-3.5 Turbo and other OpenAI models',
    icon: '🤖',
    template: {
      type: 10,
      image: "ghcr.io/krnl-labs/executor-http@sha256:07ef35b261014304a0163502a7f1dec5395c5cac1fc381dc1f79b052389ab0d5",
      attestor: "{{ENV.ATTESTOR_IMAGE}}",
      next: "prepare-authdata",
      config: {},
      inputs: [
        {
          name: "url",
          type: "string",
          value: "https://api.openai.com/v1/chat/completions",
          required: true,
          description: "OpenAI API endpoint"
        },
        {
          name: "method",
          type: "string",
          value: "POST",
          required: true,
          description: "HTTP method"
        },
        {
          name: "headers",
          type: "object",
          value: {
            "Accept": "*/*",
            "Content-Type": "application/json",
            "Authorization": "Bearer ${_SECRETS.OPENAI_API_KEY}"
          },
          required: true,
          description: "HTTP headers with API key"
        },
        {
          name: "body",
          type: "object",
          value: {
            model: "gpt-4o-mini",
            temperature: 0.7,
            messages: [
              {
                role: "system",
                content: "You are a helpful assistant."
              },
              {
                role: "user", 
                content: "Your prompt here"
              }
            ]
          },
          required: true,
          description: "Request body with model parameters"
        }
      ],
      outputs: [
        {
          name: "result",
          type: "json",
          value: "response.body.choices.0.message.content",
          required: true,
          export: true
        },
        {
          name: "usage.prompt_tokens",
          type: "number", 
          value: "response.body.usage.prompt_tokens",
          required: false,
          export: true
        },
        {
          name: "usage.completion_tokens",
          type: "number",
          value: "response.body.usage.completion_tokens", 
          required: false,
          export: true
        }
      ]
    }
  },
  {
    id: 'anthropic',
    name: 'Anthropic Claude',
    description: 'Claude 3.5 Sonnet, Claude 3 Haiku and other Anthropic models',
    icon: '🧠',
    template: {
      type: 10,
      image: "ghcr.io/krnl-labs/executor-http@sha256:07ef35b261014304a0163502a7f1dec5395c5cac1fc381dc1f79b052389ab0d5",
      attestor: "{{ENV.ATTESTOR_IMAGE}}",
      next: "prepare-authdata",
      config: {},
      inputs: [
        {
          name: "url",
          type: "string",
          value: "https://api.anthropic.com/v1/messages",
          required: true,
          description: "Anthropic API endpoint"
        },
        {
          name: "method",
          type: "string",
          value: "POST",
          required: true,
          description: "HTTP method"
        },
        {
          name: "headers",
          type: "object",
          value: {
            "Accept": "*/*",
            "Content-Type": "application/json",
            "x-api-key": "YOUR_ANTHROPIC_API_KEY",
            "anthropic-version": "2023-06-01"
          },
          required: true,
          description: "HTTP headers with API key"
        },
        {
          name: "body",
          type: "object",
          value: {
            model: "claude-sonnet-4-20250514",
            max_tokens: 1000,
            temperature: 0.7,
            messages: [
              {
                role: "user",
                content: "Your prompt here"
              }
            ]
          },
          required: true,
          description: "Request body with model parameters"
        }
      ],
      outputs: [
        {
          name: "result", 
          type: "json",
          value: "response.body.content.0.text",
          required: true,
          export: true
        },
        {
          name: "usage.input_tokens",
          type: "number",
          value: "response.body.usage.input_tokens",
          required: false,
          export: true
        },
        {
          name: "usage.output_tokens", 
          type: "number",
          value: "response.body.usage.output_tokens",
          required: false,
          export: true
        }
      ]
    }
  },
  {
    id: 'google',
    name: 'Google Gemini',
    description: 'Gemini Pro, Gemini Flash and other Google AI models',
    icon: '💎',
    template: {
      type: 10,
      image: "ghcr.io/krnl-labs/executor-http@sha256:07ef35b261014304a0163502a7f1dec5395c5cac1fc381dc1f79b052389ab0d5",
      attestor: "{{ENV.ATTESTOR_IMAGE}}",
      next: "prepare-authdata",
      config: {},
      inputs: [
        {
          name: "url",
          type: "string",
          value: "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=YOUR_GOOGLE_API_KEY",
          required: true,
          description: "Google AI API endpoint"
        },
        {
          name: "method",
          type: "string",
          value: "POST",
          required: true,
          description: "HTTP method"
        },
        {
          name: "headers",
          type: "object",
          value: {
            "Accept": "*/*",
            "Content-Type": "application/json"
          },
          required: true,
          description: "HTTP headers"
        },
        {
          name: "body",
          type: "object",
          value: {
            contents: [
              {
                parts: [
                  {
                    text: "Your prompt here"
                  }
                ]
              }
            ],
            generationConfig: {
              temperature: 0.7,
              maxOutputTokens: 1000
            }
          },
          required: true,
          description: "Request body with model parameters"
        }
      ],
      outputs: [
        {
          name: "result",
          type: "json", 
          value: "response.body.candidates.0.content.parts.0.text",
          required: true,
          export: true
        },
        {
          name: "usage.prompt_token_count",
          type: "number",
          value: "response.body.usageMetadata.promptTokenCount",
          required: false,
          export: true
        },
        {
          name: "usage.candidates_token_count",
          type: "number",
          value: "response.body.usageMetadata.candidatesTokenCount", 
          required: false,
          export: true
        }
      ]
    }
  },
  {
    id: 'mistral',
    name: 'Mistral AI',
    description: 'Mistral Large, Mistral Small and other Mistral models',
    icon: '🌪️',
    template: {
      type: 10,
      image: "ghcr.io/krnl-labs/executor-http@sha256:07ef35b261014304a0163502a7f1dec5395c5cac1fc381dc1f79b052389ab0d5",
      attestor: "{{ENV.ATTESTOR_IMAGE}}",
      next: "prepare-authdata",
      config: {},
      inputs: [
        {
          name: "url",
          type: "string",
          value: "https://api.mistral.ai/v1/chat/completions",
          required: true,
          description: "Mistral AI API endpoint"
        },
        {
          name: "method",
          type: "string",
          value: "POST",
          required: true,
          description: "HTTP method"
        },
        {
          name: "headers",
          type: "object",
          value: {
            "Accept": "*/*",
            "Content-Type": "application/json",
            "Authorization": "Bearer YOUR_MISTRAL_API_KEY"
          },
          required: true,
          description: "HTTP headers with API key"
        },
        {
          name: "body",
          type: "object",
          value: {
            model: "mistral-small-2506",
            temperature: 0.7,
            messages: [
              {
                role: "user",
                content: "Your prompt here"
              }
            ]
          },
          required: true,
          description: "Request body with model parameters"
        }
      ],
      outputs: [
        {
          name: "result",
          type: "json",
          value: "response.body.choices.0.message.content",
          required: true,
          export: true
        },
        {
          name: "usage.prompt_tokens",
          type: "number",
          value: "response.body.usage.prompt_tokens",
          required: false,
          export: true
        },
        {
          name: "usage.completion_tokens",
          type: "number",
          value: "response.body.usage.completion_tokens",
          required: false,
          export: true
        }
      ]
    }
  },
  {
    id: 'cohere',
    name: 'Cohere',
    description: 'Command R+, Command Light and other Cohere models',
    icon: '🔗',
    template: {
      type: 10,
      image: "ghcr.io/krnl-labs/executor-http@sha256:07ef35b261014304a0163502a7f1dec5395c5cac1fc381dc1f79b052389ab0d5",
      attestor: "{{ENV.ATTESTOR_IMAGE}}",
      next: "prepare-authdata",
      config: {},
      inputs: [
        {
          name: "url",
          type: "string",
          value: "https://api.cohere.ai/v2/chat",
          required: true,
          description: "Cohere API endpoint"
        },
        {
          name: "method",
          type: "string",
          value: "POST",
          required: true,
          description: "HTTP method"
        },
        {
          name: "headers",
          type: "object",
          value: {
            "Accept": "*/*",
            "Content-Type": "application/json",
            "Authorization": "Bearer YOUR_COHERE_API_KEY"
          },
          required: true,
          description: "HTTP headers with API key"
        },
        {
          name: "body",
          type: "object",
          value: {
            model: "command-a-03-2025",
            messages: [
              {
                role: "user",
                content: "Your prompt here"
              }
            ],
            temperature: 0.7,
            max_tokens: 1000
          },
          required: true,
          description: "Request body with model parameters"
        }
      ],
      outputs: [
        {
          name: "result",
          type: "json",
          value: "response.body.message.content",
          required: true,
          export: true
        },
        {
          name: "usage.tokens.input_tokens",
          type: "number",
          value: "response.body.meta.tokens.input_tokens",
          required: false,
          export: true
        },
        {
          name: "usage.tokens.output_tokens",
          type: "number", 
          value: "response.body.meta.tokens.output_tokens",
          required: false,
          export: true
        }
      ]
    }
  },
  {
    id: 'perplexity',
    name: 'Perplexity AI',
    description: 'Perplexity Sonar models with web search capabilities',
    icon: '🔍',
    template: {
      type: 10,
      image: "ghcr.io/krnl-labs/executor-http@sha256:07ef35b261014304a0163502a7f1dec5395c5cac1fc381dc1f79b052389ab0d5",
      attestor: "{{ENV.ATTESTOR_IMAGE}}",
      next: "prepare-authdata",
      config: {},
      inputs: [
        {
          name: "url",
          type: "string",
          value: "https://api.perplexity.ai/chat/completions",
          required: true,
          description: "Perplexity AI API endpoint"
        },
        {
          name: "method",
          type: "string",
          value: "POST",
          required: true,
          description: "HTTP method"
        },
        {
          name: "headers",
          type: "object",
          value: {
            "Accept": "*/*",
            "Content-Type": "application/json",
            "Authorization": "Bearer YOUR_PERPLEXITY_API_KEY"
          },
          required: true,
          description: "HTTP headers with API key"
        },
        {
          name: "body",
          type: "object",
          value: {
            model: "sonar-pro",
            temperature: 0.7,
            messages: [
              {
                role: "system",
                content: "You are a helpful assistant with access to current web information."
              },
              {
                role: "user",
                content: "Your prompt here"
              }
            ]
          },
          required: true,
          description: "Request body with model parameters"
        }
      ],
      outputs: [
        {
          name: "result",
          type: "json",
          value: "response.body.choices.0.message.content",
          required: true,
          export: true
        },
        {
          name: "usage.prompt_tokens",
          type: "number",
          value: "response.body.usage.prompt_tokens",
          required: false,
          export: true
        },
        {
          name: "usage.completion_tokens",
          type: "number",
          value: "response.body.usage.completion_tokens",
          required: false,
          export: true
        }
      ]
    }
  }
];